<script lang="ts" setup generic="T extends {}">
import type {
  TUiType,
  TUiTypeValues,
  NewReportTableRow,
  UiColor,
  StreetActionsWithCorrectness,
} from '@/types/api/reports'
import { t } from '@/locales/i18n'
import GwDateItem from '@/components/lib/GwTable/GwDateItem.vue'
import GwTruncateText from '@/components/lib/GwTable/GwTruncateText.vue'
import GwBoardItem from '@/components/lib/GwTable/GwBoardItem.vue'
import GwPositionItem from '@/components/lib/GwTable/GwPositionItem.vue'
import GwPercentageDecimalItem from '@/components/lib/GwTable/GwPercentageDecimalItem.vue'
import GwDecimalItem from '@/components/lib/GwTable/GwDecimalItem.vue'
import GwUnitItem from '@/components/lib/GwTable/GwUnitItem.vue'
import GwBlindsItem from '@/components/lib/GwTable/GwBlindsItem.vue'
import GwSiteItem from '@/components/lib/GwTable/GwSiteItem.vue'
import GwStatusItem from '@/components/lib/GwTable/GwStatusItem.vue'
import GtoScore from '@/components/lib/GwTable/GtoScore.vue'
import StreetAction from '@/components/lib/GwTable/StreetAction.vue'
import * as R from 'remeda'
import RangeItem from './RangeItem.vue'
import { getGameTableTitle } from './gwTableUtils'

export type TableCellComponentProps = {
  uiType?: TUiType
  value: TUiTypeValues[keyof TUiTypeValues] | NewReportTableRow[keyof NewReportTableRow]
  severities?: UiColor[]
  isTableValue?: boolean
  isFilterValue?: boolean
}

defineProps<TableCellComponentProps>()
</script>
<template>
  <div
    v-if="value === undefined"
    style="padding-right: 1rem"
  />
  <GwDateItem
    v-else-if="uiType === 'date'"
    :value="value as TUiTypeValues['date']"
  />
  <GwTruncateText
    v-else-if="uiType === 'text'"
    :value="value as TUiTypeValues['text']"
  />
  <GwBoardItem
    v-else-if="uiType === 'hand'"
    :board="value as TUiTypeValues['hand']"
  />
  <div v-else-if="uiType === 'boards'">
    <GwBoardItem
      v-for="board in [(value as TUiTypeValues['boards'])[0]]"
      :key="board"
      :board="board"
    />
  </div>
  <GwStatusItem
    v-else-if="uiType === 'status'"
    :value="value as TUiTypeValues['status']"
    langPath="hhv3.statuses."
    :hasTitle="!isTableValue"
    :hasTooltip="isTableValue"
  />
  <GwSiteItem
    v-else-if="uiType === 'site'"
    :onlyIcon="isFilterValue"
    :iconAndText="!isFilterValue && !isTableValue"
    :iconAndTooltip="isTableValue"
    :icon="value === 'PokerWizard' ? 'mdi-cards-playing' : ''"
    :value="value as TUiTypeValues['site']"
  />
  <GwBlindsItem
    v-else-if="uiType === 'big_blinds'"
    :value="value as TUiTypeValues['big_blinds']"
  />
  <GwUnitItem
    v-else-if="uiType === 'big_blind'"
    :value="value as TUiTypeValues['big_blind']"
    unit="bb"
    :severities
  />
  <GwPositionItem
    v-else-if="uiType === 'player_position'"
    :position="value as TUiTypeValues['player_position']"
  />
  <GwPositionItem
    v-else-if="uiType === 'opponent_position'"
    :position="value as TUiTypeValues['opponent_position']"
  />
  <GwPercentageDecimalItem
    v-else-if="uiType === 'percentage_decimal_one'"
    :value="value as TUiTypeValues['percentage_decimal_one']"
    :severities
    :isTableValue
    :hasUnit="!isTableValue"
  />
  <GwDecimalItem
    v-else-if="uiType === 'decimal_two'"
    :value="value as TUiTypeValues['decimal_two']"
    :isTableValue
    :severities
  />
  <GwDecimalItem
    v-else-if="uiType === 'decimal_three'"
    :value="(R.isString(value) ? parseFloat(value) : value) as TUiTypeValues['decimal_three']"
    :decimals="3"
    :isTableValue
    :severities
  />
  <RangeItem
    v-else-if="uiType === 'range_decimal_two'"
    :decimals="2"
    :scale="1"
    :value="value as TUiTypeValues['range_decimal_two']"
  />
  <RangeItem
    v-else-if="uiType === 'range_percentage_decimal_one'"
    :decimals="1"
    :scale="100"
    unit="%"
    :value="value as TUiTypeValues['range_percentage_decimal_one']"
  />
  <RangeItem
    v-else-if="uiType === 'range_big_blind'"
    :decimals="1"
    :scale="1"
    unit="bb"
    :value="value as TUiTypeValues['range_big_blind']"
  />
  <StreetAction
    v-else-if="uiType === 'actions_with_correctness'"
    :actions="value as StreetActionsWithCorrectness[]"
  />
  <GtoScore
    v-else-if="uiType === 'hand_correctness'"
    :isTableValue
    :value="(value as TUiTypeValues['hand_correctness']).toLowerCase()"
  />
  <div
    v-else-if="uiType === 'boolean_icon'"
    class="truncate"
  >
    <span
      v-html="t(`hhv3.filter.boolean.${(value as TUiTypeValues['boolean_icon']).toLowerCase()}`)"
    />
  </div>
  <div
    v-else-if="uiType === 'relative_position'"
    class="truncate"
  >
    <span v-html="t(`common.${(value as TUiTypeValues['relative_position']).toLowerCase()}`)" />
  </div>
  <div
    v-else-if="uiType === 'aggressor_defender'"
    class="truncate"
  >
    <span v-html="t(`common.${(value as TUiTypeValues['aggressor_defender']).toLowerCase()}`)" />
  </div>
  <div
    v-else-if="uiType === 'pot_type'"
    class="truncate"
  >
    <span
      v-html="value ? t(`shortcut.${(value as TUiTypeValues['pot_type']).toLowerCase()}`) : ''"
    />
  </div>
  <div
    v-else-if="uiType === 'game_format'"
    class="truncate"
  >
    <span v-html="t(`gametypes.groups.${(value as TUiTypeValues['game_format']).toLowerCase()}`)" />
  </div>
  <div
    v-else-if="uiType === 'game_table'"
    class="truncate"
  >
    <span v-html="getGameTableTitle(value as TUiTypeValues['game_table'])" />
  </div>
  <div
    v-else-if="uiType === 'source'"
    class="truncate"
  >
    <span v-html="t(`hhv3.sources.${(value as TUiTypeValues['source']).toLowerCase()}`)" />
  </div>
  <div
    v-else
    :data-tst="`ui_type_${uiType}`"
  >
    {{ value }}
  </div>
</template>
